/**
 * Exit Strategy — a ranked pathway assessment, and one sentence repeated on
 * purpose.
 *
 * THE ASSESSMENT IS ABOUT THE EVIDENCE, NOT THE FUTURE. "The most supported
 * pathway is a strategic acquisition" is a claim about the world. "Inverio's
 * assessment based on currently available evidence" is a claim about the
 * assessment — which is the only one that is true, so it appears next to every
 * judgement rather than once in a footer.
 *
 * THE RANKING IS BY EVIDENCE, AND SAYS SO. Pathways are ordered Supported →
 * Plausible → Weakly supported → Insufficient evidence, and the tier word is on
 * every row. There is no meter, no percentage and no pip count: any graduated
 * mark on a route to an exit reads as a probability, and a probability is the
 * one thing this analysis must never produce. `ExitStrategyBody` has nowhere to
 * put one, and neither does this component.
 *
 * TIES ARE CO-LEADING. `mostSupportedPathways` is a list, and when it holds more
 * than one they are shown together rather than ordered by something invisible.
 *
 * CANDIDATE FIT IS NOT ACQUISITION LIKELIHOOD. Each tier carries a sentence
 * saying what it is not, every candidate shows its counterargument, and an
 * undisclosed transaction value renders `Not disclosed` — never an estimate,
 * because an inferred deal value is a fabricated fact about two real companies.
 *
 * FOUNDER AND INDEPENDENT SIT SIDE BY SIDE on web and stack on mobile. A
 * founder and a permitted investor see both; only an active startup member sees
 * Edit, and the callback is simply absent for everyone else.
 */

import React, { useState } from 'react';
import { Text, View } from 'react-native';
import { Star } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SectionDivider } from '@/components/SectionDivider';
import { useAnalysisVisuals, useIsWideLayout, type EvidenceTone } from './analysis-visuals';
import { claimFor } from './claim-join';
import {
  AnalysisHero,
  DetailCard,
  Footnote,
  RowDivider,
  SectionCard,
  SectionHeading,
} from './AnalysisPrimitives';
import { formatCompactAmount } from '@/lib/strategic-analysis/market-calculations';
import type {
  ExitPathwayName,
  ExitPathwayTier,
  ExitStrategyBody,
  ExitThesis,
  GroundedStatement,
ClaimView,
} from '@/lib/strategic-analysis/types';
import {
  ASSESSMENT_QUALIFIER,
  DRIVER_JUDGEMENT_LABELS,
  FIT_DIMENSION_LABELS,
  FIT_TIER_EXPLANATIONS,
  FIT_TIER_LABELS,
  PATHWAY_LABELS,
  PATHWAY_ORDER,
  PATHWAY_TIER_LABELS,
  THESIS_COMPARISON_EXPLANATIONS,
  THESIS_COMPARISON_LABELS,
  pathwayDriverLabel,
} from './exit-labels';
import { APP_LOCALE } from '@/lib/format';

/** Ordering only. Not a scale, and never rendered as a number. */
const TIER_RANK: Record<ExitPathwayTier, number> = {
  supported: 0,
  plausible_with_caveats: 1,
  weakly_supported: 2,
  insufficient_evidence: 3,
};

const TIER_TONE: Record<ExitPathwayTier, EvidenceTone> = {
  supported: 'positive',
  plausible_with_caveats: 'caution',
  weakly_supported: 'unknown',
  insufficient_evidence: 'unknown',
};

interface ExitStrategyAnalysisProps {
  body: ExitStrategyBody;
  /** The EXACT thesis version the assessment read, not today's latest. */
  assessedThesis: ExitThesis | null;
  claims: ClaimView[];
  /** Only an active startup member may edit. Absent for an investor. */
  onEditThesis?: () => void;
  onInspectClaim?: (claim: ClaimView) => void;
}

type ResolvedPathway = {
  pathway: ExitPathwayName;
  tier: ExitPathwayTier;
  drivers: ExitStrategyBody['pathways'][number]['drivers'];
};

export function ExitStrategyAnalysis({
  body,
  assessedThesis,
  claims,
  onEditThesis,
  onInspectClaim,
}: ExitStrategyAnalysisProps) {
  const v = useAnalysisVisuals();
  const wide = useIsWideLayout();

  // Built from the fixed six, so a pathway with no evidence still appears —
  // an absent pathway would read as one that was ruled out. Then ordered by
  // tier, with the canonical order as a stable tie-break so two runs with the
  // same evidence cannot present the list differently.
  const pathways: ResolvedPathway[] = PATHWAY_ORDER
    .map((pathway) => {
      const found = body.pathways.find((p) => p.pathway === pathway);
      return {
        pathway,
        tier: found?.tier ?? ('insufficient_evidence' as ExitPathwayTier),
        drivers: found?.drivers ?? [],
      };
    })
    .sort(
      (a, b) =>
        TIER_RANK[a.tier] - TIER_RANK[b.tier] ||
        PATHWAY_ORDER.indexOf(a.pathway) - PATHWAY_ORDER.indexOf(b.pathway),
    );

  const [selected, setSelected] = useState<ExitPathwayName>(
    () => body.mostSupportedPathways[0] ?? pathways[0].pathway,
  );
  const current = pathways.find((p) => p.pathway === selected) ?? pathways[0];

  const columns = wide ? 'flex-row items-start gap-4' : 'gap-4';

  return (
    <View className="gap-5">
      {/* The independent assessment leads, and states what it is a claim about. */}
      <AnalysisHero
        label={body.mostSupportedPathways.length > 1 ? 'Co-leading pathways today' : 'Most supported pathway today'}
        headline={
          body.mostSupportedPathways.length > 0
            ? body.mostSupportedPathways.map((p) => PATHWAY_LABELS[p]).join(' · ')
            : 'No pathway is supported by the current evidence'
        }
        body={`${ASSESSMENT_QUALIFIER} It does not predict a transaction.`}
        badge={THESIS_COMPARISON_LABELS[body.thesisComparison]}
        accessibilityLabel={`Most supported pathway today: ${body.mostSupportedPathways.map((p) => PATHWAY_LABELS[p]).join(', ')}. ${THESIS_COMPARISON_LABELS[body.thesisComparison]}.`}
      />
      {body.mostSupportedPathways.length > 1 && (
        <Footnote>
          These are level on the evidence. They are shown together rather than ordered.
        </Footnote>
      )}

      {/* ---- The ranked pathway overview ---- */}
      <SectionDivider label="Exit pathways" marginTop={4} marginBottom={0} />
      <View
        className="rounded-3xl p-3 gap-2"
        style={{
          backgroundColor: v.chart.surface,
          borderWidth: 1,
          borderColor: v.chart.border,
          overflow: 'hidden',
        }}
      >
        <View className="px-1">
          <SectionHeading title="Exit pathways, ranked by evidence" />
          <Text className="mt-0.5 text-[11px]" style={{ color: v.colors.text.tertiary }}>
            Select a pathway to see what supports it. Ranking is evidence strength, not likelihood.
          </Text>
        </View>

        <View className="gap-2">
          {pathways.map((pathway) => (
            <PathwayRow
              key={pathway.pathway}
              pathway={pathway}
              coLeading={body.mostSupportedPathways.includes(pathway.pathway)}
              selected={selected === pathway.pathway}
              onSelect={() => setSelected(pathway.pathway)}
            />
          ))}
        </View>
      </View>

      {current && <PathwayDetail pathway={current} />}

      {/* ---- Founder thesis beside the independent assessment ---- */}
      <View className="gap-3">
        <SectionDivider label="Founder thesis and independent assessment" marginTop={4} marginBottom={4} />
        <View className={columns}>
          <View style={wide ? { flex: 1, minWidth: 0 } : undefined}>
            <FounderThesis thesis={assessedThesis} onEditThesis={onEditThesis} />
          </View>
          <View style={wide ? { flex: 1, minWidth: 0 } : undefined}>
            <IndependentSummary body={body} pathways={pathways} />
          </View>
        </View>

        {/* The alignment result, spanning both. */}
        <View className="gap-2">
          <SectionDivider label="Comparison" marginTop={4} marginBottom={4} />
          <SectionCard>
            <View className="gap-1">
            <Text className="text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
              {THESIS_COMPARISON_LABELS[body.thesisComparison]}
            </Text>
            <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
              {THESIS_COMPARISON_EXPLANATIONS[body.thesisComparison]}
            </Text>
            <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
              Compared against thesis
              {typeof assessedThesis?.version === 'number' ? ` v${assessedThesis.version}` : ''} — the
              version this assessment read. A later edit creates a new version and does not change this.
            </Text>
            </View>
          </SectionCard>
        </View>
      </View>

      {/* ---- Potential acquirers: FIT, with a counterargument each ---- */}
      {body.candidates.length > 0 && (
        <View className="gap-3">
          <SectionDivider label="Potential acquirers" marginTop={4} marginBottom={4} />
          <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
            Ranked by strategic fit, not by how likely a transaction is. Nothing here says any of
            these companies is considering an acquisition.
          </Text>

          {body.candidates.map((candidate) => {
            // EXACT KEY. `statement.includes(candidate.name)` is a substring
            // scan over every claim in the run: "Meta" matches the claim for
            // "Meta Platforms", and the reader is shown one company's sources
            // under the other's name with nothing to indicate it.
            const claim = claimFor(claims, candidate.claimKey);
            const counterClaim = claimFor(claims, candidate.counterargument.claimKey);
            const supported = candidate.dimensions.filter((d) => d.supported).length;
            return (
              <View
                key={candidate.entityId}
                className="rounded-2xl p-4 gap-2.5"
                style={{
                  backgroundColor: v.colors.surface.primary,
                  borderWidth: 1,
                  borderColor: v.colors.border.light,
                  overflow: 'hidden',
                }}
              >
                <View className="flex-row items-center gap-2">
                  <Text numberOfLines={1} className="flex-1 text-[14px] font-semibold" style={{ color: v.colors.text.primary }}>
                    {candidate.name}
                  </Text>
                  <View className="rounded-full px-2.5 py-1 shrink-0" style={{ backgroundColor: v.colors.primary[50] }}>
                    <Text numberOfLines={1} className="text-[11px] font-semibold" style={{ color: v.colors.primary[800] }}>
                      {FIT_TIER_LABELS[candidate.fitTier]}
                    </Text>
                  </View>
                </View>

                <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
                  {FIT_TIER_EXPLANATIONS[candidate.fitTier]}
                </Text>

                {/* Fit dimensions as a checklist: what is supported, and — just
                    as visibly — what is not. */}
                <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: v.colors.text.primary }}>
                  Strategic fit ({supported} of {candidate.dimensions.length} dimensions supported)
                </Text>
                {candidate.dimensions.map((dimension) => (
                  <View key={`${candidate.entityId}-${dimension.dimension}`} className="flex-row items-start gap-2">
                    <View
                      className="rounded-full shrink-0"
                      style={{
                        width: 6,
                        height: 6,
                        marginTop: 6,
                        backgroundColor: dimension.supported ? v.colors.success : v.colors.text.tertiary,
                      }}
                    />
                    <View className="flex-1 gap-0.5">
                      <View className="flex-row items-center gap-2">
                        <Text numberOfLines={2} className="flex-1 text-[13px]" style={{ color: v.colors.text.secondary }}>
                          {FIT_DIMENSION_LABELS[dimension.dimension]}
                        </Text>
                        <Text
                          numberOfLines={1}
                          className="text-[11px] font-semibold shrink-0"
                          style={{ color: dimension.supported ? v.colors.success : v.colors.text.tertiary }}
                        >
                          {dimension.supported ? 'Supported' : 'Not supported'}
                        </Text>
                      </View>
                      <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
                        {dimension.rationale}
                      </Text>
                    </View>
                  </View>
                ))}

                {/* Required by the schema, and shown every time. A candidate list
                    with no stated weakness reads as a shortlist. */}
                <View className="rounded-2xl p-3 gap-0.5" style={{ backgroundColor: v.colors.amber.light, overflow: 'hidden' }}>
                  <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: v.colors.amber.main }}>
                    Counterargument
                  </Text>
                  <Text className="text-[13px] leading-5" style={{ color: v.colors.text.primary }}>
                    {candidate.counterargument.text}
                  </Text>
                  {/* The stated weakness has its own sources when it has any.
                      A counterargument nobody can check is an assertion sitting
                      under a shortlist. */}
                  {counterClaim && onInspectClaim && (
                    <Text
                      accessibilityRole="button"
                      accessibilityLabel={`View sources for the counterargument about ${candidate.name}`}
                      onPress={() => onInspectClaim(counterClaim)}
                      className="text-[12px] font-semibold"
                      style={{ color: v.colors.primary[700] }}
                    >
                      View sources
                    </Text>
                  )}
                  {candidate.counterargument.ground === 'ai_consideration' && (
                    <AiConsiderationNote />
                  )}
                </View>

                {candidate.evidenceGaps && candidate.evidenceGaps.length > 0 && (
                  <View className="gap-0.5">
                    <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: v.colors.text.primary }}>
                      Evidence gaps
                    </Text>
                    {candidate.evidenceGaps.map((gap, i) => (
                      <Text
                        key={`${candidate.entityId}-gap-${i}`}
                        className="text-[13px] leading-5"
                        style={{ color: v.colors.text.secondary }}
                      >
                        {gap}
                      </Text>
                    ))}
                  </View>
                )}

                {claim && onInspectClaim && (
                  <Text
                    accessibilityRole="button"
                    accessibilityLabel={`View sources for ${candidate.name}`}
                    onPress={() => onInspectClaim(claim)}
                    className="text-[13px] font-semibold"
                    style={{ color: v.colors.primary[700] }}
                  >
                    View sources
                  </Text>
                )}
              </View>
            );
          })}
        </View>
      )}

      {/* ---- Comparable transactions ---- */}
      {body.comparables.length > 0 && (
        <View className="gap-2">
          <SectionDivider label="Comparable transactions" marginTop={4} marginBottom={4} />
          <SectionCard>
          <Text className="text-[12px]" style={{ color: v.colors.text.tertiary }}>
            Shown only where evidence supports them.
          </Text>
          {body.comparables.map((comparable, i) => (
            <View key={`comparable-${i}`} className="gap-1">
              <RowDivider />
              <View className="pt-3 gap-1">
              <View className="flex-row items-start gap-2">
                <Text numberOfLines={2} className="flex-1 text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
                  {comparable.buyer} acquired {comparable.target}
                </Text>
                <Text
                  numberOfLines={1}
                  className="text-[13px] font-semibold shrink-0"
                  style={{
                    color: comparable.valueDisclosed ? v.colors.text.primary : v.colors.text.tertiary,
                  }}
                >
                  {/* An undisclosed value stays undisclosed. Estimating one would
                      invent a fact about two real companies. */}
                  {comparable.valueDisclosed && comparable.disclosedValue !== undefined
                    ? formatCompactAmount(comparable.disclosedValue, comparable.disclosedCurrency ?? 'CHF')
                    : 'Not disclosed'}
                </Text>
              </View>
              <Text className="text-[11px]" style={{ color: v.colors.text.tertiary }}>
                Announced {comparable.announcedAt.slice(0, 10)}
              </Text>
              {comparable.rationale && (
                <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
                  {comparable.rationale}
                </Text>
              )}
              </View>
            </View>
          ))}
          </SectionCard>
        </View>
      )}

      {/* ---- Readiness: gaps and milestones, never promises. Last. ---- */}
      {(body.readinessGaps.length > 0 || body.milestones.length > 0) && (
        <View className="gap-3">
          <SectionDivider label="Exit readiness" marginTop={4} marginBottom={4} />
          <SectionCard>
          {body.readinessGaps.length > 0 && (
            <View className="gap-1">
              <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: v.colors.text.primary }}>
                Gaps today
              </Text>
              {body.readinessGaps.map((gap, i) => (
                <GroundedLine key={`gap-${i}`} statement={gap} />
              ))}
            </View>
          )}
          {body.milestones.length > 0 && (
            <View className="gap-1">
              <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: v.colors.text.primary }}>
                What would improve feasibility
              </Text>
              {body.milestones.map((milestone, i) => (
                <GroundedLine key={`milestone-${i}`} statement={milestone} />
              ))}
            </View>
          )}
          </SectionCard>
        </View>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// The ranked overview
// ---------------------------------------------------------------------------

function PathwayRow({
  pathway,
  coLeading,
  selected,
  onSelect,
}: {
  pathway: ResolvedPathway;
  coLeading: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const v = useAnalysisVisuals();
  const tone = v.tone(TIER_TONE[pathway.tier]);
  const supports = pathway.drivers.filter((d) => d.judgement === 'supports').length;
  const unknown = pathway.drivers.filter((d) => d.judgement === 'unknown').length;

  return (
    <View
      className="flex-row rounded-2xl overflow-hidden"
      style={{
        minHeight: 60,
        backgroundColor: selected ? v.colors.primary[50] : v.plate.background,
        borderWidth: selected ? 1.5 : 1,
        borderColor: selected ? v.colors.primary[600] : v.colors.border.light,
      }}
    >
      {/* A tier rail, not a meter: one solid colour band per tier, with the tier
          WORD next to it. A graduated bar would read as a probability. */}
      <View style={{ width: 4, backgroundColor: tone.fore }} />
      <AnimatedPressable
        onPress={onSelect}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={`${PATHWAY_LABELS[pathway.pathway]}: ${PATHWAY_TIER_LABELS[pathway.tier]}.`}
        className="flex-1 flex-row items-center gap-2 px-3 py-2.5"
        style={{ flex: 1, minHeight: 60 }}
      >
        <View className="flex-1 gap-0.5">
          <View className="flex-row items-center gap-1.5">
            {coLeading && <Star size={12} color={v.colors.primary[600]} fill={v.colors.primary[600]} />}
            <Text numberOfLines={2} className="flex-1 text-[13px] font-semibold leading-4" style={{ color: v.colors.text.primary }}>
              {PATHWAY_LABELS[pathway.pathway]}
            </Text>
          </View>
          <Text numberOfLines={2} className="text-[11px] leading-4" style={{ color: v.colors.text.tertiary }}>
            {pathway.drivers.length === 0
              ? 'No accepted observations'
              : `${supports} supporting${unknown > 0 ? ` · ${unknown} with no evidence` : ''}`}
          </Text>
        </View>
        {/* The tier word is the whole point of the row, so it wraps rather than
            truncating to "Supported by evid…". */}
        <View className="rounded-full px-2.5 py-1 shrink-0" style={{ backgroundColor: tone.back, maxWidth: '42%' }}>
          <Text numberOfLines={2} className="text-[11px] font-semibold leading-4" style={{ color: tone.fore }}>
            {PATHWAY_TIER_LABELS[pathway.tier]}
          </Text>
        </View>
      </AnimatedPressable>
    </View>
  );
}

function PathwayDetail({ pathway }: { pathway: ResolvedPathway }) {
  const v = useAnalysisVisuals();
  const tone = v.tone(TIER_TONE[pathway.tier]);

  return (
    <DetailCard
      title={PATHWAY_LABELS[pathway.pathway]}
      badge={PATHWAY_TIER_LABELS[pathway.tier]}
      badgeTone={tone}
      accessibilityLabel={`Selected pathway: ${PATHWAY_LABELS[pathway.pathway]}, ${PATHWAY_TIER_LABELS[pathway.tier]}`}
    >
      {pathway.drivers.length === 0 ? (
        <Text className="text-[13px] leading-5" style={{ color: v.colors.text.tertiary }}>
          No accepted observations for this pathway’s drivers. The tier above reflects that absence
          rather than a finding that the route is closed.
        </Text>
      ) : (
        pathway.drivers.map((driver) => (
          <View key={`${pathway.pathway}-${driver.driver}`} className="gap-0.5">
            <View className="flex-row items-center gap-2">
              <Text numberOfLines={2} className="flex-1 text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
                {pathwayDriverLabel(driver.driver)}
              </Text>
              <Text
                numberOfLines={1}
                className="text-[11px] font-semibold shrink-0"
                style={{
                  color: driver.judgement === 'supports'
                    ? v.colors.success
                    : driver.judgement === 'weakens'
                    ? v.colors.error
                    : v.colors.text.tertiary,
                }}
              >
                {DRIVER_JUDGEMENT_LABELS[driver.judgement]}
              </Text>
            </View>
            <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
              {driver.rationale}
            </Text>
          </View>
        ))
      )}
    </DetailCard>
  );
}

// ---------------------------------------------------------------------------
// Founder thesis / independent assessment
// ---------------------------------------------------------------------------

function FounderThesis({
  thesis,
  onEditThesis,
}: {
  thesis: ExitThesis | null;
  onEditThesis?: () => void;
}) {
  const v = useAnalysisVisuals();

  return (
    // Tinted by PROVENANCE, matching the market blocks: amber is
    // founder-supplied, teal is independently researched. The pair sits side by
    // side, so the colour is what tells them apart at a glance.
    <View
      className="rounded-2xl p-4 gap-2.5"
      style={{ backgroundColor: v.colors.amber.light, overflow: 'hidden' }}
    >
      <View className="flex-row items-center gap-2">
        <Text numberOfLines={1} className="flex-1 text-[14px] font-bold" style={{ color: v.colors.text.primary }}>
          Founder Exit Thesis
        </Text>
        {typeof thesis?.version === 'number' && (
          <View className="rounded-full px-2 py-1 shrink-0" style={{ backgroundColor: v.colors.surface.secondary }}>
            <Text numberOfLines={1} className="text-[11px] font-semibold" style={{ color: v.colors.text.secondary }}>
              v{thesis.version}
            </Text>
          </View>
        )}
      </View>

      {!thesis ? (
        <Text className="text-[13px] leading-5" style={{ color: v.colors.text.tertiary }}>
          No founder exit thesis was bound to this assessment.
        </Text>
      ) : (
        <>
          <Text className="text-[11px] font-semibold" style={{ color: v.colors.amber.main }}>
            Founder-provided · Not independently verified
          </Text>

          {thesis.preferredPathways.length > 0 && (
            <Row
              label="Preferred pathways"
              value={thesis.preferredPathways.map((p) => PATHWAY_LABELS[p]).join(', ')}
            />
          )}
          {thesis.openToAssessment && <Row label="Open to assessment" value="Yes" />}
          {thesis.horizonEarliestYear && thesis.horizonLatestYear && (
            <Row label="Target horizon" value={`${thesis.horizonEarliestYear}–${thesis.horizonLatestYear}`} />
          )}
          {thesis.targetValuationLow !== undefined && thesis.targetValuationHigh !== undefined && (
            <Row
              label="Target valuation"
              value={`${formatCompactAmount(thesis.targetValuationLow, thesis.targetValuationCurrency ?? 'CHF')} – ${formatCompactAmount(thesis.targetValuationHigh, thesis.targetValuationCurrency ?? 'CHF')}`}
            />
          )}
          {thesis.buyerCharacteristics && thesis.buyerCharacteristics.length > 0 && (
            <Row label="Desired buyer" value={thesis.buyerCharacteristics.join(', ')} />
          )}
          {thesis.rationale && (
            <View className="gap-0.5">
              <Text className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: v.colors.text.primary }}>
                Rationale
              </Text>
              <Text className="text-[13px] leading-5" style={{ color: v.colors.text.primary }}>
                {thesis.rationale}
              </Text>
            </View>
          )}

          <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
            Updated by startup team
            {thesis.createdAt ? ` · ${new Date(thesis.createdAt).toLocaleDateString(APP_LOCALE)}` : ''}
          </Text>

          {/* Only an active startup member gets this. An investor's view of the
              thesis is read-only, and the callback is simply absent. */}
          {onEditThesis && (
            <Text
              accessibilityRole="button"
              onPress={onEditThesis}
              className="text-[13px] font-semibold"
              style={{ color: v.colors.primary[700] }}
            >
              Edit exit thesis
            </Text>
          )}
        </>
      )}
    </View>
  );
}

function IndependentSummary({
  body,
  pathways,
}: {
  body: ExitStrategyBody;
  pathways: ResolvedPathway[];
}) {
  const v = useAnalysisVisuals();
  const supported = pathways.filter((p) => p.tier === 'supported');
  const plausible = pathways.filter((p) => p.tier === 'plausible_with_caveats');

  return (
    <View
      className="rounded-2xl p-4 gap-2.5"
      style={{ backgroundColor: v.colors.primary[50], overflow: 'hidden' }}
    >
      <Text numberOfLines={2} className="text-[14px] font-bold" style={{ color: v.colors.text.primary }}>
        Inverio Independent Assessment
      </Text>
      <Text className="text-[11px] font-semibold" style={{ color: v.colors.primary[800] }}>
        Evidence-based · Not a prediction
      </Text>

      <Row
        label="Supported by evidence"
        value={supported.length > 0 ? supported.map((p) => PATHWAY_LABELS[p.pathway]).join(', ') : 'None'}
      />
      <Row
        label="Plausible, with caveats"
        value={plausible.length > 0 ? plausible.map((p) => PATHWAY_LABELS[p.pathway]).join(', ') : 'None'}
      />
      <Row
        label="Acquirer candidates by fit"
        value={body.candidates.length > 0 ? String(body.candidates.length) : 'None verified'}
      />

      <Text className="text-[12px] leading-4" style={{ color: v.colors.text.tertiary }}>
        {ASSESSMENT_QUALIFIER}
      </Text>
    </View>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  const v = useAnalysisVisuals();
  return (
    <View className="flex-row items-start gap-2">
      <Text numberOfLines={2} className="flex-1 text-[13px]" style={{ color: v.colors.text.secondary }}>
        {label}
      </Text>
      <Text
        numberOfLines={3}
        className="text-[13px] font-semibold text-right shrink-0"
        style={{ maxWidth: '58%', color: v.colors.text.primary }}
      >
        {value}
      </Text>
    </View>
  );
}

/**
 * One statement, showing where it came from.
 *
 * Readiness gaps and milestones are produced by the model from the surrounding
 * material and nothing external verifies them. They used to render as plain
 * strings in the same type and colour as evidenced findings two cards above, so
 * an investor had no way to tell an observation from a suggestion. The label is
 * small and permanent rather than a one-off disclaimer at the top of the
 * section, because a disclaimer about everything is a disclaimer about nothing.
 */
function GroundedLine({ statement }: { statement: GroundedStatement }) {
  const v = useAnalysisVisuals();
  return (
    <View className="gap-0.5">
      <Text className="text-[13px] leading-5" style={{ color: v.colors.text.primary }}>
        {statement.text}
      </Text>
      {statement.ground === 'ai_consideration' && <AiConsiderationNote />}
    </View>
  );
}

/** The label itself, so the wording is identical everywhere it appears. */
function AiConsiderationNote() {
  const v = useAnalysisVisuals();
  return (
    <Text
      numberOfLines={2}
      className="text-[11px] leading-4"
      style={{ color: v.colors.text.tertiary }}
    >
      AI consideration — not established by a retained source.
    </Text>
  );
}
